import { Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';
import { AVAILABLE_AGE_RATING_KEYS, AgeRatingDefinitions, AgeRatingKey } from 'utils/ageRating';
import { calcSizeForDevice } from 'utils/index';
import { Platform } from 'utils/platform';

import Rating from 'components/Rating';

type ProfileRatingSelectionProps = {
  selectedRating: AgeRatingKey;
  baseRating: AgeRatingKey;
  onSelect: (key: AgeRatingKey) => void;
  isCreateMode?: boolean;
};

export const ProfileRatingSelection = ({
  selectedRating,
  baseRating,
  onSelect,
  isCreateMode = false,
}: ProfileRatingSelectionProps) => {
  const selectedRatingIndex = AVAILABLE_AGE_RATING_KEYS.findIndex((key) => key === selectedRating);
  const baseRatingIndex = AVAILABLE_AGE_RATING_KEYS.findIndex((key) => key === baseRating);

  const isRatingActive = (ratingKey: AgeRatingKey) => {
    const ratingIndex = AVAILABLE_AGE_RATING_KEYS.findIndex((key) => key === ratingKey);
    return ratingIndex <= selectedRatingIndex;
  };

  const canSelectRating = (ratingKey: AgeRatingKey) => {
    const ratingIndex = AVAILABLE_AGE_RATING_KEYS.findIndex((key) => key === ratingKey);
    if (isCreateMode) return true;
    return ratingIndex > baseRatingIndex;
  };

  const handleRatingItemPress = (ageKey: AgeRatingKey) => {
    if (canSelectRating(ageKey)) onSelect(ageKey);
  };

  const getHelperText = () => {
    if (selectedRating === '18')
      return 'Mostrando títulos de todas as classificações etárias para este perfil';

    if (selectedRating === 'L') return 'Mostrando títulos somente da classificação etária Livre';

    const allowedRatings = AVAILABLE_AGE_RATING_KEYS.slice(0, selectedRatingIndex + 1);
    const allowedTitles = allowedRatings.map((key) => AgeRatingDefinitions[key].title);
    return `Mostrando títulos das classificações etárias ${allowedTitles.join(', ')}`;
  };

  const isHorizontalLayout =
    Platform.isTV || (Platform.OS === 'web' && !Platform.isMobileWeb());

  const isWebHorizontal = Platform.OS === 'web' && isHorizontalLayout;

  const ratingCards = AVAILABLE_AGE_RATING_KEYS.map((ratingKey, index) => {
    const ageRatingDefinition = AgeRatingDefinitions[ratingKey];
    const isLastItem = index === AVAILABLE_AGE_RATING_KEYS.length - 1;

    return (
      <Pressable
        accessibilityRole={canSelectRating(ratingKey) ? 'button' : undefined}
        key={ratingKey}
        style={[
          isHorizontalLayout ? styles.tvRatingItem : styles.webRatingItem,
          isRatingActive(ratingKey) && styles.activeRatingItem,
          {
            marginRight: isLastItem ? 0 : calcSizeForDevice(16),
          },
        ]}
        onPress={() => handleRatingItemPress(ratingKey)}>
        <View style={styles.ratingHeader}>
          <Rating ageRatingKey={ratingKey} />
          <Text style={styles.ratingTitle}>{ageRatingDefinition.title}</Text>
        </View>
        <Text style={styles.ratingDescription}>{ageRatingDefinition.description}</Text>
      </Pressable>
    );
  });

  return (
    <View style={[styles.container, !isHorizontalLayout && styles.webContainer]}>
      {isWebHorizontal ? (
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={true}
          contentContainerStyle={styles.tvRatingSelection}
          style={styles.webHorizontalScrollView}>
          {ratingCards}
        </ScrollView>
      ) : (
        <View
          style={
            isHorizontalLayout ? styles.tvRatingSelection : styles.webRatingSelection
          }>
          {ratingCards}
        </View>
      )}
      <Text
        style={[
          styles.ratingHelperText,
          isHorizontalLayout
            ? styles.horizontalRatingHelperText
            : styles.webRatingHelperText,
        ]}>
        {getHelperText()}
      </Text>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flexDirection: 'column',
  },
  webContainer: {
    width: '100%',
  },
  tvRatingSelection: {
    flexDirection: 'row',
  },
  webHorizontalScrollView: {
    width: '100%',
    maxWidth: '100%',
  },
  webRatingSelection: {
    flexDirection: 'column',
    gap: 16,
  },
  tvRatingItem: {
    width: calcSizeForDevice(202),
    height: calcSizeForDevice(218),
    padding: calcSizeForDevice(16),
    flexDirection: 'column',
    borderWidth: 2,
    borderColor: 'transparent',
  },
  webRatingItem: {
    width: '100%',
    padding: calcSizeForDevice(16),
    flexDirection: 'column',
    borderWidth: 2,
    borderColor: 'transparent',
  },
  activeRatingItem: {
    borderColor: '#2240FF',
  },
  ratingHeader: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  ratingTitle: {
    fontSize: calcSizeForDevice(16),
    fontWeight: '500',
    color: '#FFF',
    marginLeft: calcSizeForDevice(21),
  },
  ratingDescription: {
    fontSize: calcSizeForDevice(16),
    marginTop: calcSizeForDevice(21),
    color: '#FFF',
  },
  ratingHelperText: {
    fontSize: calcSizeForDevice(18),
    fontWeight: '600',
    color: '#FFF',
    marginTop: calcSizeForDevice(40),
  },
  horizontalRatingHelperText: {
    alignSelf: 'center',
  },
  webRatingHelperText: {
    alignSelf: 'flex-start',
  },
});
